import { Link as RouterLink, useLocation } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { resolveBreadcrumbs, BreadcrumbCrumb } from '../navigation/breadcrumbs';
import { useInternalNavigation } from '../navigation/NavigationContext';

/**
 * A crumb as accepted by the `crumbs` override prop.
 * Pages with dynamic data (e.g. car nickname) pass already-translated labels here.
 */
export interface PageCrumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  /**
   * Override the auto-resolved breadcrumbs. Use this when a route has
   * dynamic segments that can't be resolved from the URL alone (e.g. car name).
   * Already-translated strings.
   */
  crumbs?: PageCrumb[];
}

export function PageHeader({ crumbs }: PageHeaderProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const { canGoBack, goBack } = useInternalNavigation();

  const resolvedCrumbs: PageCrumb[] =
    crumbs ??
    resolveBreadcrumbs(location.pathname).map((c: BreadcrumbCrumb) => ({
      label: t(c.labelKey),
      to: c.to,
    }));

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{ mb: 2, minHeight: 36 }}
    >
      <Breadcrumbs aria-label="breadcrumb" sx={{ minWidth: 0, flexGrow: 1 }}>
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          color="text.secondary"
          sx={{ display: 'flex', alignItems: 'center' }}
          aria-label={t('nav.home') ?? 'Home'}
        >
          <HomeIcon sx={{ fontSize: 20 }} />
        </Link>
        {resolvedCrumbs.map((crumb, idx) => {
          const isLast = idx === resolvedCrumbs.length - 1;
          if (isLast || !crumb.to) {
            return (
              <Typography
                key={idx}
                color="text.primary"
                sx={{
                  fontWeight: isLast ? 600 : 400,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: { xs: 140, sm: 260 },
                }}
              >
                {crumb.label}
              </Typography>
            );
          }
          return (
            <Link
              key={idx}
              component={RouterLink}
              to={crumb.to}
              underline="hover"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>

      {canGoBack && (
        <Tooltip title={t('common.back')}>
          <IconButton onClick={goBack} aria-label={t('common.back')} size="small">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}
